/**
 * Friendly Error Messages Mapping
 * Maps technical error messages to user-friendly messages
 */

export interface FriendlyError {
	title: string;
	message: string;
	action?: string;
	actionRoute?: string; // Optional route to navigate to when action is clicked
}

export const ERROR_MESSAGES: Record<string, FriendlyError> = {
	// ===== Data/Property Access Errors =====
	'Cannot read properties of undefined': {
		title: "We couldn't load your data",
		message: "Something went wrong while loading your information. Please refresh the page and try again.",
		action: "Refresh Page"
	},
	'Cannot read property': {
		title: "We couldn't load your data",
		message: "Something went wrong while loading your information. Please refresh the page and try again.",
		action: "Refresh Page"
	},
	'Cannot destructure property': {
		title: "We couldn't load your data",
		message: "Something went wrong while loading your information. Please refresh the page and try again.",
		action: "Refresh Page"
	},
	'undefined is not an object': {
		title: "We couldn't load your data",
		message: "Something went wrong while loading your information. Please refresh the page and try again.",
		action: "Refresh Page"
	},
	'null is not an object': {
		title: "We couldn't load your data",
		message: "Something went wrong while loading your information. Please refresh the page and try again.",
		action: "Refresh Page"
	},

	// ===== Server Errors =====
	'status code 500': {
		title: "Our server encountered a problem",
		message: "We're experiencing technical difficulties. Our team has been notified. Please try again in a few moments.",
		action: "Try Again"
	},
	'500': {
		title: "Our server encountered a problem",
		message: "We're experiencing technical difficulties. Our team has been notified. Please try again in a few moments.",
		action: "Try Again"
	},
	'Internal Server Error': {
		title: "Our server encountered a problem",
		message: "We're experiencing technical difficulties. Our team has been notified. Please try again in a few moments.",
		action: "Try Again"
	},
	'status code 502': {
		title: "Service temporarily unavailable",
		message: "The service is temporarily unavailable. Please try again in a few moments.",
		action: "Try Again"
	},
	'status code 503': {
		title: "Service temporarily unavailable",
		message: "The service is temporarily unavailable. Please try again in a few moments.",
		action: "Try Again"
	},
	'status code 504': {
		title: "Request timeout",
		message: "The request took too long to complete. Please try again.",
		action: "Try Again"
	},

	// ===== Network Errors =====
	'Network Error': {
		title: "Connection problem",
		message: "We couldn't connect to our servers. Please check your internet connection and try again.",
		action: "Retry"
	},
	'Failed to fetch': {
		title: "Connection problem",
		message: "We couldn't connect to our servers. Please check your internet connection and try again.",
		action: "Retry"
	},
	'Network request failed': {
		title: "Connection problem",
		message: "We couldn't connect to our servers. Please check your internet connection and try again.",
		action: "Retry"
	},
	'ERR_NETWORK': {
		title: "Connection problem",
		message: "We couldn't connect to our servers. Please check your internet connection and try again.",
		action: "Retry"
	},
	'ERR_INTERNET_DISCONNECTED': {
		title: "No internet connection",
		message: "Please check your internet connection and try again.",
		action: "Retry"
	},

	// ===== Not Found Errors =====
	'status code 404': {
		title: "Page not found",
		message: "We couldn't find what you're looking for. It may have been moved or deleted.",
		action: "Go to Dashboard",
		actionRoute: "/"
	},
	'404': {
		title: "Page not found",
		message: "We couldn't find what you're looking for. It may have been moved or deleted.",
		action: "Go to Dashboard",
		actionRoute: "/"
	},
	'Not Found': {
		title: "Page not found",
		message: "We couldn't find what you're looking for. It may have been moved or deleted.",
		action: "Go to Dashboard",
		actionRoute: "/"
	},

	// ===== Authentication/Authorization Errors =====
	'status code 401': {
		title: "Session expired",
		message: "Your session has expired. Please sign in again to continue.",
		action: "Sign In",
		actionRoute: "/login"
	},
	'401': {
		title: "Session expired",
		message: "Your session has expired. Please sign in again to continue.",
		action: "Sign In",
		actionRoute: "/login"
	},
	'Unauthorized': {
		title: "Session expired",
		message: "Your session has expired. Please sign in again to continue.",
		action: "Sign In",
		actionRoute: "/login"
	},
	'status code 403': {
		title: "Access denied",
		message: "You don't have permission to access this resource.",
		action: "Go to Dashboard",
		actionRoute: "/"
	},
	'403': {
		title: "Access denied",
		message: "You don't have permission to access this resource.",
		action: "Go to Dashboard",
		actionRoute: "/"
	},
	'Forbidden': {
		title: "Access denied",
		message: "You don't have permission to access this resource.",
		action: "Go to Dashboard",
		actionRoute: "/"
	},
	'interaction_in_progress': {
		title: "Please wait a moment",
		message: "We're still signing you in. Please wait for the current sign-in process to complete before continuing.",
		action: "Wait"
	},
	'interaction_required': {
		title: "Sign in required",
		message: "Please sign in to continue.",
		action: "Sign In",
		actionRoute: "/login"
	},

	// ===== Array/Map/Function Errors =====
	'is not a function': {
		title: "Something went wrong",
		message: "We encountered an unexpected error while displaying your data. Please refresh the page.",
		action: "Refresh Page"
	},
	'is not defined': {
		title: "Something went wrong",
		message: "We encountered an unexpected error. Please refresh the page.",
		action: "Refresh Page"
	},
	'Cannot read length': {
		title: "Data loading issue",
		message: "We're having trouble loading your data. Please refresh the page.",
		action: "Refresh Page"
	},

	// ===== Timeout Errors =====
	'timeout': {
		title: "Request timeout",
		message: "The request took too long to complete. Please try again.",
		action: "Try Again"
	},
	'ETIMEDOUT': {
		title: "Request timeout",
		message: "The request took too long to complete. Please try again.",
		action: "Try Again"
	},
	'ECONNABORTED': {
		title: "Request timeout",
		message: "The request took too long to complete. Please try again.",
		action: "Try Again"
	},

	// ===== Bad Request Errors =====
	'status code 400': {
		title: "Invalid request",
		message: "The request was invalid. Please check your input and try again.",
		action: "Try Again"
	},
	'400': {
		title: "Invalid request",
		message: "The request was invalid. Please check your input and try again.",
		action: "Try Again"
	},
	'Bad Request': {
		title: "Invalid request",
		message: "The request was invalid. Please check your input and try again.",
		action: "Try Again"
	},
	'too many parameters': {
		title: "Request too long",
		message: "The request is too long. Please shorten it and try again.",
		action: "Try Again"
	},
	'maximum of': {
		title: "Request too long",
		message: "The request is too long. Please shorten it and try again.",
		action: "Try Again"
	},

	// ===== Rate Limiting =====
	'status code 429': {
		title: "Too many requests",
		message: "You've made too many requests. Please wait a moment and try again.",
		action: "Wait"
	},
	'429': {
		title: "Too many requests",
		message: "You've made too many requests. Please wait a moment and try again.",
		action: "Wait"
	},
	'Too Many Requests': {
		title: "Too many requests",
		message: "You've made too many requests. Please wait a moment and try again.",
		action: "Wait"
	},
};

/**
 * Default friendly error message when no match is found
 */
export const DEFAULT_FRIENDLY_ERROR: FriendlyError = {
	title: "Something went wrong",
	message: "We encountered an unexpected error. Our team has been notified and is working on a fix. Please try again later.",
	action: "Go to Dashboard",
	actionRoute: "/"
};

/**
 * Finds a friendly error message for a given error message string
 * @param errorMessage - The technical error message to match
 * @returns FriendlyError object with user-friendly title, message, and action
 */
export const getFriendlyErrorMessage = (errorMessage: string | null | undefined): FriendlyError => {
	if (!errorMessage) {
		return DEFAULT_FRIENDLY_ERROR;
	}

	const normalizedError = errorMessage.trim();

	// Try exact match first
	if (ERROR_MESSAGES[normalizedError]) {
		return ERROR_MESSAGES[normalizedError];
	}

	// Try case-insensitive partial match
	const lowerError = normalizedError.toLowerCase();
	for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
		if (lowerError.includes(key.toLowerCase())) {
			return value;
		}
	}

	// Return default if no match found
	return DEFAULT_FRIENDLY_ERROR;
};

/**
 * Checks if an error message has a friendly mapping
 * @param errorMessage - The error message to check
 * @returns boolean indicating if a friendly message exists
 */
export const hasFriendlyMessage = (errorMessage: string | null | undefined): boolean => {
	if (!errorMessage) return false;
	
	const friendly = getFriendlyErrorMessage(errorMessage);
	return friendly.title !== DEFAULT_FRIENDLY_ERROR.title || 
		   friendly.message !== DEFAULT_FRIENDLY_ERROR.message;
};
